import React, { useState } from 'react';
import { FileText, Download, Edit, CheckCircle, AlertCircle, Info, Printer, Save } from 'lucide-react';

interface DocumentTemplate {
  id: string;
  name: string;
  description: string;
  category: 'immigration' | 'employment' | 'housing' | 'financial' | 'education' | 'healthcare';
  difficulty: 'easy' | 'medium' | 'hard';
  estimatedTime: number; // in minutes
  requiredDocuments: string[];
  steps: string[];
  tips: string[];
  isCompleted: boolean;
  lastUpdated: Date;
}

interface DocumentTemplateGeneratorProps {
  onTemplateSelect?: (template: DocumentTemplate) => void;
  userProfile?: {
    name: string;
    address: string;
    phone: string;
    email: string;
    ssn?: string;
    immigrationStatus: string;
  };
}

const DocumentTemplateGenerator: React.FC<DocumentTemplateGeneratorProps> = ({
  onTemplateSelect,
  userProfile
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [currentStep, setCurrentStep] = useState(0);

  const documentTemplates: DocumentTemplate[] = [
    {
      id: 'ssn-application',
      name: 'Social Security Number Application (SS-5)',
      description: 'Application form for obtaining a Social Security Number',
      category: 'immigration',
      difficulty: 'easy',
      estimatedTime: 30,
      requiredDocuments: ['Passport', 'I-94 Form', 'Birth Certificate', 'Marriage Certificate (if applicable)'],
      steps: [
        'Gather required documents',
        'Complete the SS-5 form',
        'Visit your local Social Security office',
        'Submit application and documents',
        'Wait for processing (2-4 weeks)'
      ],
      tips: [
        'Make copies of all documents before submitting',
        'Bring original documents and copies',
        'Check office hours before visiting',
        'Consider making an appointment to avoid long waits'
      ],
      isCompleted: false,
      lastUpdated: new Date()
    },
    {
      id: 'state-id-application',
      name: 'State ID/Driver\'s License Application',
      description: 'Application for state identification card or driver\'s license',
      category: 'immigration',
      difficulty: 'medium',
      estimatedTime: 45,
      requiredDocuments: ['SSN', 'Passport', 'I-94 Form', 'Proof of Address', 'Birth Certificate'],
      steps: [
        'Gather required documents',
        'Complete the application form',
        'Visit DMV office',
        'Take required tests (if applicable)',
        'Pay fees and get photo taken',
        'Receive temporary ID and wait for permanent one'
      ],
      tips: [
        'Check DMV requirements for your state',
        'Study for written test if getting driver\'s license',
        'Bring payment in acceptable forms',
        'Make appointment online if possible'
      ],
      isCompleted: false,
      lastUpdated: new Date()
    },
    {
      id: 'job-application',
      name: 'Standard Job Application Template',
      description: 'Template for filling out job applications',
      category: 'employment',
      difficulty: 'easy',
      estimatedTime: 20,
      requiredDocuments: ['Resume', 'References', 'Work Authorization', 'Education Certificates'],
      steps: [
        'Prepare personal information',
        'List work experience',
        'Include education background',
        'Add references',
        'Review and proofread',
        'Submit application'
      ],
      tips: [
        'Keep information consistent across applications',
        'Use action verbs in job descriptions',
        'Include relevant skills and achievements',
        'Follow up after submitting'
      ],
      isCompleted: false,
      lastUpdated: new Date()
    },
    {
      id: 'apartment-application',
      name: 'Apartment Rental Application',
      description: 'Template for apartment rental applications',
      category: 'housing',
      difficulty: 'medium',
      estimatedTime: 35,
      requiredDocuments: ['ID', 'Proof of Income', 'Bank Statements', 'References', 'Credit Report'],
      steps: [
        'Gather required documents',
        'Complete application form',
        'Provide proof of income',
        'Submit references',
        'Pay application fee',
        'Wait for approval'
      ],
      tips: [
        'Have all documents ready before applying',
        'Be honest about your income and situation',
        'Provide good references',
        'Consider getting a co-signer if needed'
      ],
      isCompleted: false,
      lastUpdated: new Date()
    },
    {
      id: 'bank-account-application',
      name: 'Bank Account Opening Application',
      description: 'Template for opening a new bank account',
      category: 'financial',
      difficulty: 'easy',
      estimatedTime: 25,
      requiredDocuments: ['ID', 'SSN', 'Proof of Address', 'Initial Deposit'],
      steps: [
        'Choose a bank and account type',
        'Gather required documents',
        'Visit bank branch or apply online',
        'Complete application form',
        'Make initial deposit',
        'Receive account information'
      ],
      tips: [
        'Compare different banks and account types',
        'Ask about fees and requirements',
        'Consider online banking options',
        'Keep account information secure'
      ],
      isCompleted: false,
      lastUpdated: new Date()
    },
    {
      id: 'tax-return-1040',
      name: 'Individual Tax Return (Form 1040)',
      description: 'Template for filing individual income tax return',
      category: 'financial',
      difficulty: 'hard',
      estimatedTime: 90,
      requiredDocuments: ['W-2 Forms', '1099 Forms', 'Bank Statements', 'Receipts for Deductions'],
      steps: [
        'Gather all tax documents',
        'Choose filing method (paper or electronic)',
        'Complete Form 1040',
        'Calculate deductions and credits',
        'Review for accuracy',
        'Submit by April 15th'
      ],
      tips: [
        'Consider using tax software or professional help',
        'Keep all tax documents for at least 3 years',
        'File for extension if needed',
        'Check for free tax preparation services'
      ],
      isCompleted: false,
      lastUpdated: new Date()
    }
  ];

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'immigration':
        return '🛂';
      case 'employment':
        return '💼';
      case 'housing':
        return '🏠';
      case 'financial':
        return '💰';
      case 'education':
        return '🎓';
      case 'healthcare':
        return '🏥';
      default:
        return '📄';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'immigration':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'employment':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'housing':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'financial':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'education':
        return 'text-indigo-600 bg-indigo-50 border-indigo-200';
      case 'healthcare':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'text-green-600 bg-green-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'hard':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const handleTemplateSelect = (template: DocumentTemplate) => {
    setSelectedTemplate(template);
    setCurrentStep(0);
    setFormData({});
    onTemplateSelect?.(template);
  };

  const generateDocument = () => {
    if (!selectedTemplate || !userProfile) return;

    // In a real app, this would generate an actual document
    const documentContent = {
      template: selectedTemplate,
      userData: userProfile,
      formData: formData,
      generatedAt: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(documentContent, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedTemplate.name.replace(/\s+/g, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printDocument = () => {
    window.print();
  };

  if (selectedTemplate) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{selectedTemplate.name}</h2>
            <p className="text-gray-600">{selectedTemplate.description}</p>
          </div>
          <button
            onClick={() => setSelectedTemplate(null)}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Back to Templates
          </button>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Document Generation Progress</h3>
            <span className="text-sm text-gray-600">
              Step {currentStep + 1} of {selectedTemplate.steps.length}
            </span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 transition-all duration-500 ease-out"
              style={{ width: `${((currentStep + 1) / selectedTemplate.steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Current Step */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {selectedTemplate.steps[currentStep]}
          </h3>
          
          {/* Required Documents */}
          {currentStep === 0 && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-800 mb-3">Required Documents:</h4>
              <ul className="space-y-2">
                {selectedTemplate.requiredDocuments.map((doc, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>{doc}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tips */}
          <div className="mb-6">
            <h4 className="font-medium text-gray-800 mb-3">Tips for this step:</h4>
            <ul className="space-y-2">
              {selectedTemplate.tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                  <Info className="w-4 h-4 text-blue-500 mt-0.5" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>
            
            {currentStep < selectedTemplate.steps.length - 1 ? (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Next Step
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={generateDocument}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Generate Document
                </button>
                <button
                  onClick={printDocument}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Document Template Generator</h2>
        <p className="text-gray-600">
          Generate and fill out common forms and documents with step-by-step guidance
        </p>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {documentTemplates.map((template) => (
          <div
            key={template.id}
            onClick={() => handleTemplateSelect(template)}
            className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-200 cursor-pointer group"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{getCategoryIcon(template.category)}</span>
                <div>
                  <h3 className="font-semibold text-gray-800 group-hover:text-primary-600 transition-colors">
                    {template.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(template.difficulty)}`}>
                      {template.difficulty}
                    </span>
                    <span className="text-xs text-gray-500">
                      {template.estimatedTime} min
                    </span>
                  </div>
                </div>
              </div>
              
              {template.isCompleted && (
                <CheckCircle className="w-6 h-6 text-green-500" />
              )}
            </div>

            {/* Description */}
            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
              {template.description}
            </p>

            {/* Category */}
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(template.category)}`}>
              <span className="capitalize">{template.category}</span>
            </div>

            {/* Steps Preview */}
            <div className="mt-4">
              <h4 className="font-medium text-gray-800 mb-2 text-sm">Steps:</h4>
              <ul className="space-y-1">
                {template.steps.slice(0, 3).map((step, index) => (
                  <li key={index} className="text-xs text-gray-600 flex items-start gap-2">
                    <span className="w-4 h-4 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xs font-medium mt-0.5 flex-shrink-0">
                      {index + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
                {template.steps.length > 3 && (
                  <li className="text-xs text-gray-500 ml-6">
                    +{template.steps.length - 3} more steps
                  </li>
                )}
              </ul>
            </div>

            {/* Required Documents Count */}
            <div className="mt-4 flex items-center gap-2 text-xs text-gray-600">
              <FileText className="w-4 h-4" />
              <span>{template.requiredDocuments.length} required documents</span>
            </div>
          </div>
        ))}
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
        <div className="flex items-start gap-3">
          <Info className="w-6 h-6 text-blue-600 mt-1" />
          <div>
            <h3 className="font-semibold text-blue-800 mb-2">Need Help?</h3>
            <p className="text-blue-700 text-sm mb-3">
              Our document templates provide step-by-step guidance for common forms and applications. 
              Each template includes required documents, helpful tips, and detailed instructions.
            </p>
            <ul className="text-blue-700 text-sm space-y-1">
              <li>• All templates are based on official government forms</li>
              <li>• Step-by-step guidance for each document</li>
              <li>• Tips and best practices included</li>
              <li>• Printable and downloadable formats available</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentTemplateGenerator;
